<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="mb-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="modal"
              data-bs-target="#add_leaves"
              class="btn btn-primary d-flex align-items-center"
              v-can="'Leaves.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>Apply Leave
            </a>
          </div>
          <div class="head-icons ms-2">
            <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="row">
        <div
          v-for="(card, idx) in typeCards"
          :key="card.leave_type_id || card.name"
          class="col-xl-3 col-md-6"
        >
          <div class="card" :class="cardBgClass(idx)">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between">
                <div class="text-start">
                  <p class="mb-1">{{ card.name }}</p>
                  <h6>
                    Booked
                    <small>{{ formatCount(card.booked) }}</small>
                  </h6>
                </div>
                <div class="flex-shrink-0 me-2">
                  <span class="avatar avatar-md d-flex">
                    <i :class="cardIcon(idx)" class="fs-32"></i>
                  </span>
                </div>
              </div>
              <span class="badge" :class="cardBadgeClass(idx)">
                Available : {{ formatCount(card.remaining) }}
                <span v-if="card.carried_forward" class="ms-1">(+{{ formatCount(card.carried_forward) }} carried)</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <div class="d-flex flex-wrap align-items-center gap-2">
            <h5 class="me-2 mb-0">Leave List</h5>
            <span class="badge bg-primary-transparent">
              Total Days Taken : {{ formatCount(store.summary?.totals?.total_taken) }}
            </span>
            <span class="badge bg-secondary-transparent">
              Total Days Untaken : {{ formatCount(store.summary?.totals?.total_remaining) }}
            </span>
          </div>

          <div class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
            <div class="me-3">
              <select class="form-select" v-model="store.filters.status" @change="reload()">
                <option value="">All Status</option>
                <option value="New">Pending Manager</option>
                <option value="ManagerApproved">Awaiting HR</option>
                <option value="Approved">Approved</option>
                <option value="Declined">Declined</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
              </div>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <leaves-employee-table
              :rows="store.items"
              :loading="store.loading"
              @edit="onEdit"
              @delete="onDelete"
            />
          </div>
        </div>

        <div
          class="card-footer d-flex align-items-center justify-content-between flex-wrap gap-2"
        >
          <div>
            Showing page {{ store.pagination.current_page }} of {{ totalPages }}
            <span v-if="store.pagination.total">
              ({{ store.pagination.total }} records)
            </span>
          </div>
          <div class="d-flex gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="store.pagination.current_page <= 1 || store.loading"
              @click="goToPage(store.pagination.current_page - 1)"
            >
              Prev
            </button>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="store.pagination.current_page >= totalPages || store.loading"
              @click="goToPage(store.pagination.current_page + 1)"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <leaves-employee-modal ref="modalRef" />
</template>

<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, onMounted, computed, nextTick } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";
import { useEmployeeLeaveStore } from "@/stores/employeeLeaveStore";
import LeavesEmployeeTable from "./leaves-employee-table.vue";
import LeavesEmployeeModal from "@/components/modal/leaves-employee-modal.vue";

const title = "Leaves";
const text = "Employee";
const text1 = "Leaves Employee";

const dateRangeInput = ref(null);
const store = useEmployeeLeaveStore();
const modalRef = ref(null);

const CARD_BGS = ["bg-black-le", "bg-blue-le", "bg-purple-le", "bg-pink-le"];
const CARD_BADGES = [
  "bg-secondary-transparent",
  "bg-info-transparent",
  "bg-transparent-purple",
  "bg-pink-transparent",
];
const CARD_ICONS = [
  "ti ti-calendar-event",
  "ti ti-vaccine",
  "ti ti-hexagon-letter-c",
  "ti ti-hexagonal-prism-plus",
];

const totalPages = computed(() => {
  const t = store.pagination.total || 0;
  const p = store.pagination.per_page || 25;
  return Math.max(1, Math.ceil(t / p));
});

const typeCards = computed(() => {
  const types = store.summary?.types ?? [];
  const primary = types.filter((t) => t.count_in_other === false);
  const otherTypes = types.filter((t) => t.count_in_other !== false);

  const cards = primary.map((t) => ({
    leave_type_id: t.leave_type_id,
    name: t.name,
    booked: t.booked ?? (t.taken ?? 0) + (t.applied ?? 0),
    remaining: t.remaining ?? 0,
    carried_forward: t.carried_forward ?? 0,
  }));

  if (otherTypes.length) {
    cards.push({
      leave_type_id: null,
      name: "Other Leaves",
      booked: otherTypes.reduce(
        (a, c) => a + (c.booked ?? (c.taken ?? 0) + (c.applied ?? 0)),
        0
      ),
      remaining: otherTypes.reduce((a, c) => a + (c.remaining ?? 0), 0),
    });
  }

  return cards;
});

function cardBgClass(idx) {
  return CARD_BGS[idx % CARD_BGS.length];
}
function cardBadgeClass(idx) {
  return CARD_BADGES[idx % CARD_BADGES.length];
}
function cardIcon(idx) {
  return CARD_ICONS[idx % CARD_ICONS.length];
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function formatCount(n) {
  const num = Number(n ?? 0);
  return Number.isFinite(num) ? num : 0;
}

function reload() {
  store.fetchAll({ page: 1 });
}

function goToPage(page) {
  store.fetchAll({ page });
}

function onEdit(record) {
  modalRef.value?.openEdit(record);
}

function onDelete(record) {
  modalRef.value?.openDelete(record);
}

function initDateRange() {
  if (!dateRangeInput.value) return;
  const start = moment().startOf("year");
  const end = moment().endOf("year");

  function setLabel(s, e) {
    dateRangeInput.value.value = `${s.format("DD/MM/YYYY")} - ${e.format("DD/MM/YYYY")}`;
    store.filters.date_from = s.format("YYYY-MM-DD");
    store.filters.date_to = e.format("YYYY-MM-DD");
    reload();
  }

  new DateRangePicker(
    dateRangeInput.value,
    {
      startDate: start,
      endDate: end,
      autoUpdateInput: false,
      ranges: {
        Today: [moment(), moment()],
        Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "This Year": [moment().startOf("year"), moment().endOf("year")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [
          moment().subtract(1, "month").startOf("month"),
          moment().subtract(1, "month").endOf("month"),
        ],
      },
    },
    setLabel
  );

  setLabel(start, end);
}

onMounted(async () => {
  await Promise.all([store.fetchSummary(), store.fetchAll()]);
  nextTick(initDateRange);
});
</script>
